import React, { useState } from "react";
import { compact, find, map, memoize } from "lodash";
import BasicSettings from "./BasicSettings";
import JoinSettings from "./JoinSettings";
import MemberSettings from "./MemberSettings";
import MailSettings, { createDaysArray } from "./MailSettings";
import TabBar from "../../shared/TabBar";
import { FormProvider, useForm } from "react-hook-form";
import FormActions from "components/shared/form/fields/FormActions";
import { onValidationError } from "helpers/formValidations";

const getTabs = memoize(() => {
  const tabs = [
    {
      label: I18n.t("js.administration.network_settings.basic_settings.title"),
      TabContent: BasicSettings,
    },
    {
      label: I18n.t("js.administration.network_settings.join_settings.title"),
      TabContent: JoinSettings,
    },
    {
      label: I18n.t("js.administration.network_settings.member_settings.title"),
      TabContent: MemberSettings,
    },
    {
      label: I18n.t("js.administration.network_settings.mail_settings.title"),
      TabContent: MailSettings,
    },
  ];

  return tabs;
});

type FormData = {
  two_factor_totp_available?: boolean;
  two_factor_email_available?: boolean;
  [key: string]: any;
};

type NetworkSettingsFormTypes = {
  initialValues: { [key: string]: any };
  onSubmit: (values: any) => Promise<any>;
};

function NetworkSettingsForm({
  onSubmit,
  initialValues,
}: NetworkSettingsFormTypes) {
  const [selectedTabIndex, selectTabIndex] = useState(0);
  const { TabContent } = getTabs()[selectedTabIndex];

  const methods = useForm<FormData>({
    values: {
      ...initialValues,
      language: {
        value: initialValues.language || Preload.current_network.language,
        label: I18n.t(
          `js.networks.languages.${
            initialValues.language || Preload.current_network.language
          }`,
        ),
      },
      group_promotion_config: {
        ...initialValues.group_promotion_config,
        group_category_ids: map(
          initialValues.group_promotion_config.group_category_ids,
          (id) => {
            const gc = find(Preload.current_network.group_categories, { id });
            return { value: id, label: gc?.name };
          },
        ),
        groups: map(initialValues.group_promotion_config.groups, (group) => ({
          id: group.id,
          value: group.id,
          name: group.name,
          type: "group",
        })),
      },
      directory_profile_fields:
        initialValues?.directory_profile_fields &&
        map(initialValues?.directory_profile_fields, (field) =>
          find(Preload.current_network.profile_fields, { name: field }),
        ).map((field) => field && { value: field.name, label: field.label }),
      detection_profile_field:
        initialValues?.detection_profile_field &&
        find(Preload.current_network.profile_fields_without_multiline, {
          name: initialValues?.detection_profile_field,
        }),
      directory_filter_profile_fields:
        initialValues?.directory_filter_profile_fields &&
        map(initialValues?.directory_filter_profile_fields, (field) =>
          find(Preload.current_network.profile_fields, {
            name: field,
          }),
        ).map((field) => field && { value: field.name, label: field.label }),
      digest_sending_days:
        initialValues?.digest_sending_days &&
        compact(
          map(initialValues?.digest_sending_days, (value) => {
            const days = createDaysArray();
            const day = find(days, { index: value.day });
            if (!day) return null;

            return {
              key: `${value.day}_${value.time}`,
              value,
              label: `${day.name} ${I18n.t(
                `js.administration.network_settings.digests.${value.time}`,
              )}`,
            };
          }),
        ),
      birthday_emails: {
        ...initialValues.birthday_emails,
        image: initialValues.birthday_emails.image && [
          initialValues.birthday_emails.image,
        ],
      },
    },
  });
  const { handleSubmit, watch } = methods;

  const two_factor_totp_available = watch("two_factor_totp_available");
  const two_factor_email_available = watch("two_factor_email_available");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onValidationError)}>
        <TabBar
          tabs={getTabs()}
          selectedTabIndex={selectedTabIndex}
          selectTabIndex={selectTabIndex}
        />
        <div className="tab-content network-settings mt-4">
          <TabContent />
        </div>
        <div className="form-horizontal mt-4">
          <FormActions
            saveDisabled={
              !two_factor_totp_available && !two_factor_email_available
            }
          />
        </div>
      </form>
    </FormProvider>
  );
}

export default NetworkSettingsForm;
